/* eslint-disable no-param-reassign */
import type { Draft, PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { pick, uniqueId } from "lodash-es";
import { castDraft } from "immer";
import { findOnlyOrThrow, lastOrThrow } from "@dhau/lang-extras";
import type {
	BaseplateSize,
	NumberOfBaseplates,
} from "@brickme/project-core/src/model/project-primitives.ts";
import type {
	BrickedPictureOutput,
	ImageZoomOffset,
	PictureConfiguration,
	BrickPosition,
	DetailFilter,
	PenMark,
	Dithering,
} from "@brickme/project-core/src/model/picture.ts";
import type { UnixTimestamp } from "@brickme/project-core/src/model/time.ts";
import type { BrickColour } from "@brickme/project-core/src/model/bricks.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import type { FaceBoundingBox } from "@brickme/project-core/src/model/project.ts";
import type { BitmapMask } from "@brickme/project-core/src/model/bitmap-mask.ts";
import roundAndConstrainImageZoomOffset from "@brickme/project-core/src/utils/round-and-constrain-image-zoom-offset.ts";
import { maxImageZoom } from "@brickme/project-core/src/model/picture.ts";
import {
	originPosition,
	multiplyPosition,
	arePositionsClose,
	roundPosition,
	multiplySize,
} from "@brickme/project-core/src/model/geom.ts";
import {
	maxKnownBasePlateSize,
	maxNumberOfBaseplates,
} from "@brickme/project-core/src/model/project-primitives.ts";
import { createBrickedImageFromSourceNoCacheSync } from "@brickme/project-core/src/image-build/create-bricked-image-from-source.ts";
import createMaxRequiredResolutionSourceImage from "@brickme/project-core/src/image-build/create-max-required-resolution-source-image.ts";
import { createFacesMask } from "@brickme/project-core/src/model/bitmap-mask.ts";
import type { EncodedImage } from "@brickme/project-core/src/utils/encoded-image.ts";
import type { BitmapPosteriseImageResult } from "@brickme/project-core/src/model/posterise.ts";
import { loadBitmapFromUrlToMaxSize } from "@brickme/frontend-common/src/bitmap/image-parsing.ts";
import bitmapToSmallestSizeEncodedImage from "@brickme/frontend-common/src/bitmap/bitmap-to-smallest-size-encoded-image.ts";
import type {
	GetPicToBrickByIdQuery,
	GetPicToBrickByIdVariables,
} from "~/api/get-pic-to-brick-by-id.ts";
import getPicToBrickById from "~/api/get-pic-to-brick-by-id.ts";
import type { State as ReferenceState } from "~/features/reference/state.ts";
import type { StoreThunkApiConfig } from "~/store-config.ts";
import type { State as UserState } from "../user/store-slice.ts";
import type { State as GiftState } from "../gift/store-slice.ts";
import type { OnceOffLoad } from "~/utils/loading.ts";
import { ensureLoadedOnceOffLoad, isOnceOffLoaded } from "~/utils/loading.ts";
import encodedImageArrayBufferToBitmap from "~/utils/encoded-image-array-buffer-to-bitmap.ts";
import createDefaultPictureConfiguration from "./create-default-picture-configuration.ts";
import type { State } from "./state.ts";
import { requireOpenProject } from "./state.ts";
import addPenMarksReducer from "./reducers/add-pen-marks-reducer.ts";
import setAutoPaletteModeReducer from "./reducers/set-auto-palette-mode-reducer.ts";
import deselectCustomModePaletteBrick from "./actions/deselect-custom-mode-palette-brick.ts";
import startProject from "./start-project.ts";
import creatorProjectToBuildSources from "./creator-project-to-build-sources.ts";

const name = "workspace";

const selectImage = createAsyncThunk<
	void,
	{
		readonly fileName: string;
		readonly originalEncodedImage: EncodedImage | undefined;
		readonly inputImage: Bitmap;
	},
	StoreThunkApiConfig<{
		reference: ReferenceState;
		user: UserState;
		gift: GiftState;
	}>
>(
	`${name}/selectImage`,
	async (
		{
			/* fileName, */ originalEncodedImage: providedOriginalBuffer,
			inputImage,
		},
		{ getState, dispatch },
	) => {
		const {
			reference: { basePlateSizes, initialNumberOfBaseplates },
			gift: { giftRedemption },
		} = getState();

		// For gift redemption, use the larger of default or gift size
		let constrainedNumberOfBasePlates = initialNumberOfBaseplates;
		if (giftRedemption) {
			const defaultConfig = createDefaultPictureConfiguration(
				basePlateSizes,
				inputImage.width,
				inputImage.height,
				undefined, // Use default calculation
			);

			// Compare total baseplate counts, not individual dimensions
			const defaultTotalBaseplates =
				defaultConfig.numberOfBasePlates.width *
				defaultConfig.numberOfBasePlates.height;
			const giftTotalBaseplates =
				giftRedemption.giftSize.width * giftRedemption.giftSize.height;

			if (defaultTotalBaseplates <= giftTotalBaseplates) {
				// Use the larger of the two (gift size if larger, default if same)
				constrainedNumberOfBasePlates =
					giftTotalBaseplates > defaultTotalBaseplates
						? giftRedemption.giftSize
						: defaultConfig.numberOfBasePlates;
			} else {
				// Default is too large, use gift dimensions
				constrainedNumberOfBasePlates = {
					width: giftRedemption.giftSize.width,
					height: giftRedemption.giftSize.height,
				};
			}
		}

		const config = createDefaultPictureConfiguration(
			basePlateSizes,
			inputImage.width,
			inputImage.height,
			constrainedNumberOfBasePlates,
		);

		const sizedDownBitmap = createMaxRequiredResolutionSourceImage(
			inputImage,
			maxNumberOfBaseplates,
		);
		// If we've resized the image, then we can't use the original buffer because it's a different size.
		// This would result in a mismatch between local image and remote image sizes.
		// I've seen it in action and the bg mask ended up being a different size to source, which is a
		// problem.
		const originalEncodedImage =
			sizedDownBitmap.width === inputImage.width &&
			sizedDownBitmap.height === inputImage.height
				? providedOriginalBuffer
				: undefined;
		const smallestEncodedImage = await bitmapToSmallestSizeEncodedImage(
			sizedDownBitmap,
			originalEncodedImage,
		);
		// Careful, we want to use the bitmap extracted from the optimised version of the image,
		// not the un optimised image. Reason is we want design to be consistent. Otherwise subsequent
		// loads would use the optimised version whilst initial would use unoptimised.
		const smallestBitmap =
			await encodedImageArrayBufferToBitmap(smallestEncodedImage);

		await dispatch(
			startProject({
				project: {
					currentPicture: config,
					faceBoundingBoxes: undefined,
					otherVersions: [],
					updatedAt: Date.now(),
				},
				// source: {
				//   type: "userImage",
				//   fileName
				// },
				sourceImageBitmapOrUrl: {
					bitmap: smallestBitmap,
					originalEncodedImage,
					smallestEncodedImage,
				},
				backgroundMaskImageUrl: undefined,
				enhanceFacesImageUrl: undefined,
				posteriseImage: undefined,
				colorisationImageUrl: undefined,
			}),
		);
	},
);

const selectImageByUrl = createAsyncThunk<
	void,
	string,
	StoreThunkApiConfig<{
		reference: ReferenceState;
		user: UserState;
		gift: GiftState;
	}>
>(`${name}/selectImageByUrl`, async (imageUrl, { dispatch }) => {
	const maxResolution =
		maxNumberOfBaseplates * maxKnownBasePlateSize * maxImageZoom;
	const inputImage = await loadBitmapFromUrlToMaxSize(imageUrl, maxResolution);
	// TODO: Download buffer to use here
	await dispatch(
		selectImage({
			fileName: lastOrThrow(imageUrl.split("/")),
			originalEncodedImage: undefined,
			inputImage,
		}),
	);
});

const selectImageByPicToBrick = createAsyncThunk<
	void,
	string,
	StoreThunkApiConfig<{
		reference: ReferenceState;
		user: UserState;
		gift: GiftState;
	}>
>(
	`${name}/selectImageByPicToBrick`,
	async (picId, { dispatch, extra: { apiClient } }) => {
		const result = await apiClient.request<
			GetPicToBrickByIdQuery,
			GetPicToBrickByIdVariables
		>(getPicToBrickById, { id: picId });
		if (!result.picToBrickById) {
			throw new Error(`No pic to brick with id ${picId}`);
		}
		// TODO: Going down this route creates a copy. We should be referencing the same image
		// for each customer using this
		await dispatch(selectImageByUrl(result.picToBrickById.imageUrl));
	},
);

const resetProject = createAsyncThunk<
	{ configuration: PictureConfiguration; build: BrickedPictureOutput },
	undefined,
	StoreThunkApiConfig<{ reference: ReferenceState; workspace: State }>
>(`${name}/resetProject`, (_, { getState }) => {
	const {
		workspace: { openProject },
		reference: { systemPalette, initialNumberOfBaseplates, basePlateSizes },
	} = getState();
	const systemPaletteData = ensureLoadedOnceOffLoad(
		systemPalette,
		"System palette not loaded",
	);
	if (!openProject) {
		throw new Error("No open project");
	}

	const { project } = openProject;
	const configuration = createDefaultPictureConfiguration(
		basePlateSizes,
		project.sourceImage.bitmap.width,
		project.sourceImage.bitmap.height,
		initialNumberOfBaseplates,
	);
	const build = createBrickedImageFromSourceNoCacheSync(
		creatorProjectToBuildSources(project),
		systemPaletteData,
		configuration,
	);
	return { configuration, build };
});

const setPictureConfiguration = createAsyncThunk<
	BrickedPictureOutput,
	PictureConfiguration,
	StoreThunkApiConfig<{ reference: ReferenceState; workspace: State }>
>(`${name}/setPictureConfiguration`, (config, { getState }) => {
	const {
		reference: { systemPalette },
		workspace: { openProject },
	} = getState();
	const systemPaletteData = ensureLoadedOnceOffLoad(
		systemPalette,
		"System palette not loaded",
	);
	if (!openProject) {
		throw new Error("No open project");
	}

	const { project } = openProject;
	return createBrickedImageFromSourceNoCacheSync(
		creatorProjectToBuildSources(project),
		systemPaletteData,
		config,
	);
});

const switchToSavedVersion = createAsyncThunk<
	BrickedPictureOutput,
	string,
	StoreThunkApiConfig<{ workspace: State; reference: ReferenceState }>
>(`${name}/switchToSavedVersion`, (versionId, { getState }) => {
	const {
		workspace: { openProject },
		reference: { systemPalette },
	} = getState();
	const systemPaletteData = ensureLoadedOnceOffLoad(
		systemPalette,
		"System palette not loaded",
	);
	if (!openProject) {
		throw new Error("No open project");
	}
	const version = findOnlyOrThrow(
		openProject.project.otherVersions,
		(o) => o.id === versionId,
		`Couldn't find version ${versionId}`,
	);

	return createBrickedImageFromSourceNoCacheSync(
		creatorProjectToBuildSources(openProject.project),
		systemPaletteData,
		version.picture,
	);
});

const setCustomPaletteMode = createAsyncThunk<
	readonly BrickColour[],
	undefined,
	StoreThunkApiConfig<{ reference: ReferenceState; workspace: State }>
>(`${name}/setCustomPaletteMode`, (_, { getState }) => {
	// TODO: Race condition here. If already building then won't have latest brickCount
	const {
		workspace: { openProject },
		reference: { systemPalette },
	} = getState();
	if (!openProject) {
		throw new Error("No open project");
	}
	const systemPaletteData = ensureLoadedOnceOffLoad(
		systemPalette,
		"System palette not loaded",
	);

	return systemPaletteData
		.filter((p) => p.brick.hexString in openProject.build.brickCounts)
		.map((p) => p.brick);
});

const selectCustomModePaletteBrick = createAsyncThunk<
	readonly BrickColour[],
	BrickColour,
	StoreThunkApiConfig<{ workspace: State; reference: ReferenceState }>
>(
	`${name}/selectCustomModePaletteBrick`,
	// Required for cross state access
	// eslint-disable-next-line @typescript-eslint/require-await
	async (colour, { getState }) => {
		const {
			workspace: { openProject },
			reference: { systemPalette },
		} = getState();
		if (!openProject) {
			throw new Error("No open project");
		}
		const systemPaletteData = ensureLoadedOnceOffLoad(
			systemPalette,
			"System palette not loaded",
		);

		let newPalette: BrickColour[];
		if (openProject.project.currentPicture.paletteMode.type === "custom") {
			newPalette =
				openProject.project.currentPicture.paletteMode.palette.slice();
		} else {
			// TODO: Race condition here. If already building then won't have latest brickCount
			newPalette = systemPaletteData
				.map((p) => p.brick)
				.filter((s) => s.hexString in openProject.build.brickCounts);
		}
		newPalette.push(colour);
		return newPalette;
	},
);

function positionToKey(position: BrickPosition) {
	return `${position.x},${position.y}`;
}

const slice = createSlice({
	name,
	initialState: {
		openProject: undefined,
	} as State,
	reducers: {
		startNewProject: (state) => {
			state.openProject = undefined;
		},
		applyPaintBucket: (
			state: Draft<State>,
			action: PayloadAction<{
				readonly colour: BrickColour;
				readonly positions: readonly BrickPosition[];
			}>,
		) => {
			const start = performance.now();
			const { project } = requireOpenProject(state);
			const { currentPicture } = project;

			const { colour, positions } = action.payload;

			let newPens: PenMark[] = [];
			const newPositions = new Set(positions.map(positionToKey));

			// Allow overriding of old pen positions
			currentPicture.pen.forEach((p) => {
				if (!newPositions.has(positionToKey(p.position))) {
					newPens.push(p);
				}
			});
			newPens = newPens.concat(
				positions.map((position) => ({
					colour,
					position,
				})),
			);

			// Add new pen positions
			currentPicture.pen = newPens;

			currentPicture.updatedAt = Date.now();
			project.updatedAt = currentPicture.updatedAt;

			if (import.meta.env.MODE === "development") {
				// eslint-disable-next-line no-console
				console.log(
					`PaintBucket applied ${Math.round(performance.now() - start)}ms`,
				);
			}
		},
		setImageZoom(state: Draft<State>, action: PayloadAction<number>) {
			const { project } = requireOpenProject(state);
			const { currentPicture } = project;
			const previousZoom = currentPicture.imageZoom;
			if (previousZoom === action.payload) {
				console.warn("Setting zoom to current value");
				return;
			}
			currentPicture.imageZoom = action.payload;
			currentPicture.imageZoomOffset = roundAndConstrainImageZoomOffset(
				project.sourceImage.bitmap,
				castDraft(currentPicture),
				roundPosition(
					multiplyPosition(
						currentPicture.imageZoomOffset,
						action.payload / previousZoom,
					),
				),
			);
			// Kind of messy to try and scale this up
			if (currentPicture.pen.length > 0) {
				currentPicture.pen = [];
			}
			currentPicture.updatedAt = Date.now();
			project.updatedAt = currentPicture.updatedAt;
		},
		setImageZoomOffset(state, action: PayloadAction<ImageZoomOffset>) {
			const openProject = requireOpenProject(state);
			const { currentPicture } = openProject.project;

			const newOffset = roundAndConstrainImageZoomOffset(
				openProject.project.sourceImage.bitmap,
				openProject.project.currentPicture,
				action.payload,
			);
			if (arePositionsClose(newOffset, currentPicture.imageZoomOffset, 0.5)) {
				console.warn("zoom offset didn't change");
				return;
			}

			currentPicture.imageZoomOffset = newOffset;
			currentPicture.updatedAt = Date.now();
			openProject.project.updatedAt = currentPicture.updatedAt;
		},
		addPenMarks: addPenMarksReducer,
		setNumberOfBasePlates(state, action: PayloadAction<NumberOfBaseplates>) {
			const openProject = requireOpenProject(state);
			const picture = openProject.project.currentPicture;
			picture.imageZoomOffset = originPosition;
			picture.numberOfBasePlates = action.payload;
			picture.numberOfBricks = multiplySize(
				picture.numberOfBasePlates,
				picture.basePlateSize,
			);
			picture.pen = [];

			// Changing size might mean that frame can no longer support it
			// if (
			//   picture.frame &&
			//   !isFrameCompatibleWithProject(picture.frame, picture)
			// ) {
			//   picture.frame = undefined;
			// }
			openProject.project.currentPicture.updatedAt = Date.now();
			openProject.project.updatedAt =
				openProject.project.currentPicture.updatedAt;
		},
		setBasePlateSize(
			state: Draft<State>,
			action: PayloadAction<BaseplateSize>,
		) {
			const { openProject } = state;
			if (!openProject) {
				throw new Error("No project");
			}
			const picture = openProject.project.currentPicture;
			picture.imageZoomOffset = roundPosition(
				multiplyPosition(
					picture.imageZoomOffset,
					action.payload / picture.basePlateSize,
				),
			);
			picture.basePlateSize = action.payload;
			picture.numberOfBricks = multiplySize(
				picture.numberOfBasePlates,
				picture.basePlateSize,
			);
			picture.pen = [];

			// Changing size might mean that frame can no longer support it
			// if (
			//   picture.frame &&
			//   !isFrameCompatibleWithProject(picture.frame, picture)
			// ) {
			//   picture.frame = undefined;
			// }
			picture.updatedAt = Date.now();
			openProject.project.updatedAt = picture.updatedAt;
		},
		setBrightness(state: Draft<State>, action: PayloadAction<number>) {
			const { project } = requireOpenProject(state);
			project.currentPicture.brightness = action.payload;
			project.currentPicture.updatedAt = Date.now();
			project.updatedAt = project.currentPicture.updatedAt;
		},
		setContrast: (state: Draft<State>, action: PayloadAction<number>) => {
			const { project } = requireOpenProject(state);
			project.currentPicture.contrast = action.payload;
			project.currentPicture.updatedAt = Date.now();
			project.updatedAt = project.currentPicture.updatedAt;
		},
		removeOtherVersion: (
			state: Draft<State>,
			action: PayloadAction<string>,
		) => {
			const { project } = requireOpenProject(state);
			const otherVersionIndex = project.otherVersions.findIndex(
				(o) => o.id === action.payload,
			);
			if (otherVersionIndex >= 0) {
				project.otherVersions.splice(otherVersionIndex, 1);
			}
			// Try and trigger a re-save
			project.updatedAt = Date.now();
		},
		setDetailFilter: (
			state: Draft<State>,
			action: PayloadAction<DetailFilter | undefined>,
		) => {
			const { project } = requireOpenProject(state);
			project.currentPicture.detailFilter = action.payload;
			project.currentPicture.updatedAt = Date.now();
			project.updatedAt = project.currentPicture.updatedAt;
		},
		setRemoveBackground: (
			state: Draft<State>,
			action: PayloadAction<PictureConfiguration["removeBackground"]>,
		) => {
			const { project } = requireOpenProject(state);
			project.currentPicture.removeBackground = action.payload;
			project.currentPicture.updatedAt = Date.now();
			project.updatedAt = project.currentPicture.updatedAt;
		},
		setFixFaceColours(state: Draft<State>, action: PayloadAction<boolean>) {
			const { project } = requireOpenProject(state);
			project.currentPicture.fixFaceColours = action.payload;
			project.currentPicture.posterise = false;
			// Deprecated filter. Removing now when people make changes
			project.currentPicture.enhanceFaces = false;
			project.currentPicture.updatedAt = Date.now();
			project.updatedAt = project.currentPicture.updatedAt;
		},
		setPosterise(state: Draft<State>, action: PayloadAction<boolean>) {
			const { project } = requireOpenProject(state);
			project.currentPicture.posterise = action.payload;
			project.currentPicture.fixFaceColours = false;
			// Deprecated filter. Removing now when people make changes
			project.currentPicture.enhanceFaces = false;
			project.currentPicture.updatedAt = Date.now();
			project.updatedAt = project.currentPicture.updatedAt;
		},
		setDithering(
			state: Draft<State>,
			action: PayloadAction<Dithering | undefined>,
		) {
			const { project } = requireOpenProject(state);
			project.currentPicture.dithering = action.payload;
			project.currentPicture.updatedAt = Date.now();
			project.updatedAt = project.currentPicture.updatedAt;
		},
		setSaturation(state: Draft<State>, action: PayloadAction<number>) {
			const { project } = requireOpenProject(state);
			project.currentPicture.saturation = action.payload;
			project.currentPicture.updatedAt = Date.now();
			project.updatedAt = project.currentPicture.updatedAt;
		},
		setAutoPaletteMode: setAutoPaletteModeReducer,
		setBuild(
			state: Draft<State>,
			action: PayloadAction<{
				output: BrickedPictureOutput;
				timestamp: UnixTimestamp;
			}>,
		) {
			const { output, timestamp } = action.payload;
			if (Number.isNaN(timestamp)) {
				// Need to avoid this at all costs because blocks up the build pipeline
				throw new Error("NaN timestamp for new build");
			}
			const openProject = requireOpenProject(state);
			openProject.builtCurrentPictureUpdatedAt = timestamp;
			openProject.build = output as Draft<BrickedPictureOutput>;
		},
		// Don't want this action. i.e. should be an async action within workspace.
		// However need to merge saved project and workspace for that to work
		setBackgroundMaskImage: (
			state: Draft<State>,
			action: PayloadAction<OnceOffLoad<BitmapMask>>,
		) => {
			const openProject = requireOpenProject(state);
			openProject.project.backgroundMask = action.payload as Draft<
				OnceOffLoad<BitmapMask>
			>;
			if (
				openProject.project.facesMask === undefined &&
				isOnceOffLoaded(openProject.project.backgroundMask) &&
				openProject.project.faceBoundingBoxes !== undefined
			) {
				openProject.project.facesMask = createFacesMask(
					openProject.project.backgroundMask.data,
					openProject.project.faceBoundingBoxes,
				) as Draft<BitmapMask>;
			}
			openProject.project.currentPicture.updatedAt = Date.now();
		},
		setColorisationImage: (
			state: Draft<State>,
			action: PayloadAction<OnceOffLoad<Bitmap>>,
		) => {
			const openProject = requireOpenProject(state);
			openProject.project.colorisationImage = action.payload;
			openProject.project.currentPicture.updatedAt = Date.now();
		},
		setEnhanceFacesImage: (
			state: Draft<State>,
			action: PayloadAction<OnceOffLoad<Bitmap>>,
		) => {
			const openProject = requireOpenProject(state);
			openProject.project.enhanceFacesImage = action.payload;
			openProject.project.currentPicture.updatedAt = Date.now();
		},
		setPosteriseImageResult: (
			state: Draft<State>,
			action: PayloadAction<OnceOffLoad<BitmapPosteriseImageResult>>,
		) => {
			const openProject = requireOpenProject(state);
			openProject.project.posteriseImage = action.payload;
			openProject.project.currentPicture.updatedAt = Date.now();
		},
		setFaceBoundingBoxes: (
			state: Draft<State>,
			action: PayloadAction<
				readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[]
			>,
		) => {
			const openProject = requireOpenProject(state);
			openProject.project.faceBoundingBoxes = action.payload as Pick<
				FaceBoundingBox,
				"x" | "y" | "width" | "height"
			>[];
			if (
				openProject.project.facesMask === undefined &&
				isOnceOffLoaded(openProject.project.backgroundMask) &&
				openProject.project.faceBoundingBoxes !== undefined
			) {
				openProject.project.facesMask = createFacesMask(
					openProject.project.backgroundMask.data,
					openProject.project.faceBoundingBoxes,
				) as Draft<BitmapMask>;
			}
			openProject.project.currentPicture.updatedAt = Date.now();
		},
		addSavedVersionFromCurrent: (state: Draft<State>) => {
			const openProject = requireOpenProject(state);
			openProject.project.otherVersions.push({
				id: uniqueId(),
				picture: openProject.project.currentPicture,
			});
			openProject.project.updatedAt = Date.now();
		},
	},
	extraReducers(builder) {
		builder.addCase(
			deselectCustomModePaletteBrick.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<
					ReturnType<(typeof deselectCustomModePaletteBrick)["fulfilled"]>
				>,
			) => {
				const { openProject } = state;
				if (!openProject) {
					throw new Error("No open project");
				}

				const { project } = openProject;
				project.currentPicture.paletteMode = {
					type: "custom",
					palette: action.payload,
				};
				// TODO: Only if a change
				project.currentPicture.updatedAt = Date.now();
				project.updatedAt = openProject.project.currentPicture.updatedAt;
			},
		);
		builder.addCase(
			switchToSavedVersion.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof switchToSavedVersion)["fulfilled"]>>,
			) => {
				const openProject = requireOpenProject(state);
				const oldCurrent = openProject.project.currentPicture;
				const versionId = action.meta.arg;
				const otherVersion = findOnlyOrThrow(
					openProject.project.otherVersions,
					(o) => o.id === versionId,
					`Couldn't find version ${versionId}`,
				);

				openProject.project.currentPicture = otherVersion.picture;
				openProject.build = action.payload;
				const otherVersionIndex = openProject.project.otherVersions.findIndex(
					(o) => o.id === versionId,
				);
				if (otherVersionIndex >= 0) {
					openProject.project.otherVersions.splice(otherVersionIndex, 1);
				}
				openProject.project.otherVersions.unshift({
					id: uniqueId(),
					picture: oldCurrent,
				});
				// A little bit of a hack to force a rebuild
				openProject.builtCurrentPictureUpdatedAt = -1;
				openProject.project.updatedAt = Date.now();
			},
		);
		builder.addCase(
			setPictureConfiguration.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<
					ReturnType<(typeof setPictureConfiguration)["fulfilled"]>
				>,
			) => {
				const openProject = requireOpenProject(state);
				openProject.project.currentPicture = action.meta.arg;
				openProject.build = action.payload;
				openProject.project.updatedAt = Date.now();
			},
		);
		builder.addCase(
			startProject.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof startProject)["fulfilled"]>>,
			) => {
				state.openProject = action.payload;
			},
		);
		builder.addCase(
			setCustomPaletteMode.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof setCustomPaletteMode)["fulfilled"]>>,
			) => {
				const { project } = requireOpenProject(state);
				project.currentPicture.paletteMode = {
					type: "custom",
					palette: action.payload,
				};
				project.currentPicture.updatedAt = Date.now();
				project.updatedAt = project.currentPicture.updatedAt;
			},
		);
		builder.addCase(
			resetProject.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof resetProject)["fulfilled"]>>,
			) => {
				if (!state.openProject) {
					throw new Error("Expected open project");
				}

				const now = Date.now();
				state.openProject = {
					build: action.payload.build,
					builtCurrentPictureUpdatedAt: now,
					project: {
						currentPicture: action.payload.configuration,
						otherVersions: [],
						...pick(
							state.openProject.project,
							"sourceImage",
							"backgroundMask",
							"enhanceFacesImage",
							"posteriseImage",
							"colorisationImage",
							"faceBoundingBoxes",
							"facesMask",
						),
						updatedAt: now,
					},
				};
			},
		);
		builder.addCase(
			selectCustomModePaletteBrick.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<
					ReturnType<(typeof selectCustomModePaletteBrick)["fulfilled"]>
				>,
			) => {
				const { openProject } = state;
				if (!openProject) {
					throw new Error("No open project");
				}
				// TODO: Only if change
				openProject.project.currentPicture.updatedAt = Date.now();
				openProject.project.updatedAt =
					openProject.project.currentPicture.updatedAt;
				openProject.project.currentPicture.paletteMode = {
					type: "custom",
					palette: action.payload,
				};
			},
		);
	},
});

export const {
	startNewProject,
	setImageZoom,
	setImageZoomOffset,
	setAutoPaletteMode,
	setBasePlateSize,
	setBrightness,
	setContrast,
	setDetailFilter,
	setNumberOfBasePlates,
	setSaturation,
	addPenMarks,
	setRemoveBackground,
	setBuild,
	applyPaintBucket,
	removeOtherVersion,
	setFixFaceColours,
	setPosterise,
	setDithering,
	setBackgroundMaskImage,
	setEnhanceFacesImage,
	setPosteriseImageResult,
	setFaceBoundingBoxes,
	setColorisationImage,
	addSavedVersionFromCurrent,
} = slice.actions;
export {
	setCustomPaletteMode,
	selectImage,
	setPictureConfiguration,
	selectImageByUrl,
	switchToSavedVersion,
	selectImageByPicToBrick,
	resetProject,
	selectCustomModePaletteBrick,
};
export default slice.reducer;
